import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { useAuth } from "./firebase/auth";
import {
  watchConversations,
  watchGroups,
  isConversationVisibleToAgent,
  computeMyQueueIds,
  WhatsappConversation,
  WhatsappGroup,
} from "../model/whatsapp.model";
import { getQueues } from "../model/queue.model";
import { formatTitleWithUnread, shouldShowUnreadIndicator } from "../utils/unreadIndicator";

// Pra mídia sem legenda, lastMessageText é só o nome do arquivo gerado no
// backend (ex: "arquivo-recebido.ogg") — cru demais pra aparecer numa
// notificação. Mostra um rótulo por tipo em vez disso; só texto puro usa
// lastMessageText de verdade. Mesmo enum em conversa 1:1 e grupo, por isso
// um mapa só cobre os dois.
const MESSAGE_TYPE_LABEL: Partial<Record<WhatsappConversation["lastMessageType"] & string, string>> = {
  image: "📷 Foto",
  audio: "🎤 Áudio",
  video: "🎥 Vídeo",
  document: "📄 Documento",
  unsupported: "Mensagem não suportada",
};

function formatMessagePreview(conversation: WhatsappConversation): string {
  const type = conversation.lastMessageType;
  if (type && MESSAGE_TYPE_LABEL[type]) return MESSAGE_TYPE_LABEL[type]!;
  return conversation.lastMessageText || "Nova mensagem";
}

// Grupo tem vários remetentes possíveis — sem o nome de quem mandou, a
// notificação ("Nome do grupo" / "mensagem") fica sem dizer quem falou,
// diferente da conversa 1:1 onde title=contato já resolve isso sozinho.
function formatGroupMessagePreview(group: WhatsappGroup): string {
  const type = group.lastMessageType;
  const body =
    type && MESSAGE_TYPE_LABEL[type]
      ? MESSAGE_TYPE_LABEL[type]!
      : group.lastMessageText || "Nova mensagem";
  return group.lastMessageSenderName ? `${group.lastMessageSenderName}: ${body}` : body;
}

// Acha (ou cria, se por algum motivo faltar) o <link rel="icon"> — não
// hardcoda caminho de imagem, então funciona igual pra qualquer favicon já
// carregado (inclusive white-label, ver public/comhub/favicon.ico).
function getFaviconLink(): HTMLLinkElement {
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  return link;
}

// Desenha o favicon atual num canvas e soma uma bolinha vermelha no canto —
// funciona com qualquer favicon (não precisa de asset novo por marca).
// Não testável em jsdom (canvas é removido antes do teste neste repo, ver
// CLAUDE.md) — só verificado manualmente no navegador.
function createBadgedFaviconUrl(baseHref: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const size = 32;
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("sem contexto 2d"));
        return;
      }
      ctx.drawImage(img, 0, 0, size, size);

      const dotRadius = size * 0.28;
      const cx = size - dotRadius;
      const cy = dotRadius;
      ctx.beginPath();
      ctx.arc(cx, cy, dotRadius, 0, Math.PI * 2);
      ctx.fillStyle = "#fff";
      ctx.fill();
      ctx.beginPath();
      ctx.arc(cx, cy, dotRadius * 0.72, 0, Math.PI * 2);
      ctx.fillStyle = "#e53e3e";
      ctx.fill();

      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = () => reject(new Error("falha ao carregar o favicon"));
    img.src = baseHref;
  });
}

// Provider de topo (mesmo nível de SoftphoneProvider em App.tsx) — precisa
// rodar independente de qual página o atendente está, não só enquanto
// Whatsapp.page.tsx está montada (o listener de lá é local e some ao sair
// da rota /whatsapp). Só dispara pra mensagem recebida ENQUANTO a janela
// está aberta — é um listener rodando na página viva, sem service worker/
// push, então não tem como (nem deveria) notificar nada com o navegador
// fechado; ao reabrir, o primeiro snapshot só registra o estado atual sem
// notificar (ver isFirstSnapshotRef/isFirstGroupSnapshotRef), então
// mensagens que chegaram enquanto a janela estava fechada nunca viram
// notificação atrasada.
interface WhatsappNotificationsContextValue {
  // Preenchido quando o atendente clica numa notificação — Whatsapp.page.tsx
  // observa isso pra selecionar a conversa/grupo certo, mesmo se a página
  // já estiver montada (troca de rota não recarrega quem já está em
  // /whatsapp).
  pendingConversationId: string | null;
  clearPendingConversationId: () => void;
  pendingGroupId: string | null;
  clearPendingGroupId: () => void;
}

const WhatsappNotificationsContext = createContext<WhatsappNotificationsContextValue>({
  pendingConversationId: null,
  clearPendingConversationId: () => {},
  pendingGroupId: null,
  clearPendingGroupId: () => {},
});

export function useWhatsappNotifications() {
  return useContext(WhatsappNotificationsContext);
}

export function WhatsappNotificationsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const auth = useAuth();
  const history = useHistory();
  const [pendingConversationId, setPendingConversationId] = useState<string | null>(
    null,
  );
  const [pendingGroupId, setPendingGroupId] = useState<string | null>(null);
  const [queues, setQueues] = useState<{ id?: string; participants?: { id?: string }[] }[]>([]);

  // unreadCount conhecido por conversa, só pra detectar aumento (mensagem
  // nova de verdade) — nunca decrementa sozinho aqui, quem zera é o
  // backend (abrir a conversa) refletindo de volta pelo próprio listener.
  const prevUnreadRef = useRef<Record<string, number>>({});
  // Primeiro snapshot é sempre o estado JÁ existente (inclusive não lidas
  // de antes de logar) — sem essa guarda, toda conversa com unread > 0 no
  // login disparava uma notificação de mensagem "nova" que não é nova.
  const isFirstSnapshotRef = useRef(true);

  // Mesma lógica da conversa 1:1, mas por atendente (unreadByUser) em vez
  // de um unreadCount só — grupo não tem "dono" único.
  const prevGroupUnreadRef = useRef<Record<string, number>>({});
  const isFirstGroupSnapshotRef = useRef(true);

  // Título/favicon com indicador de não lida — mesmo escopo "é meu"
  // (isConversationVisibleToAgent/accessUserIds) que já decide quando
  // disparar a Notification, só que aqui soma o total em vez de comparar
  // delta por item. NÃO depende de foco da aba (mudou 2026-09-22, ver
  // comentário em utils/unreadIndicator.ts) — reflete unreadCount de
  // verdade, só some com reply ou "marcar como lida" (mesmo sinal que já
  // zera o contador na lista de conversas).
  const [conversationsUnreadTotal, setConversationsUnreadTotal] = useState(0);
  const [groupsUnreadTotal, setGroupsUnreadTotal] = useState(0);
  const originalTitleRef = useRef<string>(
    typeof document !== "undefined" ? document.title : "",
  );
  const originalFaviconHrefRef = useRef<string | null>(null);
  const badgedFaviconUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (typeof document === "undefined") return;
    originalFaviconHrefRef.current = getFaviconLink().href;
  }, []);

  const totalUnread = conversationsUnreadTotal + groupsUnreadTotal;

  useEffect(() => {
    if (typeof document === "undefined") return;

    if (!shouldShowUnreadIndicator(totalUnread)) {
      document.title = originalTitleRef.current;
      if (originalFaviconHrefRef.current) {
        getFaviconLink().href = originalFaviconHrefRef.current;
      }
      return;
    }

    document.title = formatTitleWithUnread(originalTitleRef.current, totalUnread);

    if (badgedFaviconUrlRef.current) {
      getFaviconLink().href = badgedFaviconUrlRef.current;
      return;
    }
    if (!originalFaviconHrefRef.current) return;
    createBadgedFaviconUrl(originalFaviconHrefRef.current)
      .then((url) => {
        badgedFaviconUrlRef.current = url;
        // Só aplica se ainda deve estar mostrando o indicador — evita
        // trocar o ícone de volta pra "com bolinha" depois que o total já
        // zerou (reply/marcar como lida) enquanto a imagem carregava.
        if (shouldShowUnreadIndicator(totalUnread)) {
          getFaviconLink().href = url;
        }
      })
      .catch(() => {
        // Falha ao gerar o favicon com bolinha (ex: favicon ausente) não
        // deveria impedir o título de mudar — já aconteceu antes.
      });
  }, [totalUnread]);

  useEffect(() => {
    if (typeof Notification === "undefined") return;
    // Mesmo gatilho que o softphone já usa pra ligação recebida
    // (SipService.js) — chamar de novo aqui é seguro (idempotente) e cobre
    // quem usa WhatsApp sem nunca ter recebido uma ligação.
    if (Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, []);

  useEffect(() => {
    const companyId = auth?.user?.token?.claims?.company;
    if (!companyId) return;
    getQueues(auth).then(setQueues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.user?.token?.claims?.company]);

  const myId = auth?.user?.uid;
  const myQueueIds = useMemo(
    () => computeMyQueueIds(queues, myId || ""),
    [queues, myId],
  );

  useEffect(() => {
    const companyId = auth?.user?.token?.claims?.company;
    if (!companyId || !myId) return;

    prevUnreadRef.current = {};
    isFirstSnapshotRef.current = true;

    const unsubscribe = watchConversations(companyId, (conversations) => {
      // Bug real corrigido (2026-09-15): sem esse filtro, notificava TODOS
      // os atendentes da empresa pra qualquer conversa, mesmo já assumida
      // por outro atendente — mesma regra que a lista de conversas já
      // aplicava (ver isConversationVisibleToAgent), só que faltava aqui.
      const visible = conversations.filter((c) =>
        isConversationVisibleToAgent(c, myId, myQueueIds),
      );

      setConversationsUnreadTotal(
        visible.reduce((sum, c) => sum + (c.unreadCount || 0), 0),
      );

      if (isFirstSnapshotRef.current) {
        visible.forEach((c) => {
          prevUnreadRef.current[c.id] = c.unreadCount || 0;
        });
        isFirstSnapshotRef.current = false;
        return;
      }

      visible.forEach((c) => {
        const prevUnread = prevUnreadRef.current[c.id] || 0;
        const unread = c.unreadCount || 0;
        if (unread > prevUnread) notify(c);
        prevUnreadRef.current[c.id] = unread;
      });
    });

    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.user?.token?.claims?.company, myId, myQueueIds]);

  useEffect(() => {
    const companyId = auth?.user?.token?.claims?.company;
    const myId = auth?.user?.uid;
    if (!companyId || !myId) return;

    prevGroupUnreadRef.current = {};
    isFirstGroupSnapshotRef.current = true;

    const unsubscribe = watchGroups(companyId, (groups) => {
      // Mesmo filtro de acesso que useWhatsappGroups já aplica pra decidir
      // o que aparece na lista — sem isso, notificaria sobre grupo
      // restrito a outros atendentes que este aqui nem consegue abrir.
      const visible = groups.filter(
        (g) =>
          !g.accessUserIds || g.accessUserIds.length === 0 || g.accessUserIds.includes(myId),
      );

      setGroupsUnreadTotal(
        visible.reduce((sum, g) => sum + (g.unreadByUser?.[myId] || 0), 0),
      );

      if (isFirstGroupSnapshotRef.current) {
        visible.forEach((g) => {
          prevGroupUnreadRef.current[g.id] = g.unreadByUser?.[myId] || 0;
        });
        isFirstGroupSnapshotRef.current = false;
        return;
      }

      visible.forEach((g) => {
        const prevUnread = prevGroupUnreadRef.current[g.id] || 0;
        const unread = g.unreadByUser?.[myId] || 0;
        if (unread > prevUnread) notifyGroup(g);
        prevGroupUnreadRef.current[g.id] = unread;
      });
    });

    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.user?.token?.claims?.company, auth?.user?.uid]);

  const notify = (conversation: WhatsappConversation) => {
    if (typeof Notification === "undefined" || Notification.permission !== "granted")
      return;

    const notification = new Notification(
      conversation.contactName || "Nova mensagem no WhatsApp",
      {
        body: formatMessagePreview(conversation),
        // Mensagens seguidas da MESMA conversa substituem a notificação
        // anterior em vez de empilhar uma por cima da outra.
        tag: `whatsapp-conversation-${conversation.id}`,
      },
    );
    notification.onclick = () => {
      window.focus();
      setPendingConversationId(conversation.id);
      history.push("/whatsapp");
      notification.close();
    };
  };

  const notifyGroup = (group: WhatsappGroup) => {
    if (typeof Notification === "undefined" || Notification.permission !== "granted")
      return;

    const notification = new Notification(group.name || "Novo grupo no WhatsApp", {
      body: formatGroupMessagePreview(group),
      tag: `whatsapp-group-${group.id}`,
    });
    notification.onclick = () => {
      window.focus();
      setPendingGroupId(group.id);
      history.push("/whatsapp");
      notification.close();
    };
  };

  return (
    <WhatsappNotificationsContext.Provider
      value={{
        pendingConversationId,
        clearPendingConversationId: () => setPendingConversationId(null),
        pendingGroupId,
        clearPendingGroupId: () => setPendingGroupId(null),
      }}
    >
      {children}
    </WhatsappNotificationsContext.Provider>
  );
}
